import { z } from 'zod';

/** CONTRACT §2: the largest file MyRewards takes; the WAF's file-upload limit matches it. */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/**
 * Why a file over that limit is refused, in one set of words (APP-148 M4): the server's 413 and a
 * page that refuses it before it's sent say the same thing.
 */
export const FILE_TOO_LARGE_WORDS = 'Files can be up to 25 MB.';

/** What an attachment is for (PRD §13). */
export const DocumentPurpose = z.enum([
  'write_up',
  /** The request that started a Sector or Executive Discretionary Bonus (SL3), added at entry. */
  'original_ask',
  /** APP-148: what a Stock Admin was asked to do for an Executive or Sector Lead. Always staff-only. */
  'asked_for',
  'proof',
  'tax_estimate',
  'letter',
  'earning_statement',
  'other',
]);
export type DocumentPurpose = z.infer<typeof DocumentPurpose>;

/**
 * `pending`: waiting for the malware scan (never downloadable). `clean` and `not_required` can be
 * downloaded; `malicious` never can. `unscannable`: the scan couldn't check it, or never did,
 * within a day of upload (APP-206), so it can't be opened and needs uploading again.
 */
export const ScanStatus = z.enum(['pending', 'clean', 'malicious', 'not_required', 'unscannable']);
export type ScanStatus = z.infer<typeof ScanStatus>;

export const DocumentView = z.object({
  id: z.uuid(),
  purpose: DocumentPurpose,
  /** The person's line the file belongs to; null for the whole award. */
  lineId: z.uuid().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  scanStatus: ScanStatus,
  /** Never shown to employees (for example the ADP QuickCalc screenshot). */
  staffOnly: z.boolean(),
  /** Null for files the system made (letters). */
  uploadedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  uploadedAt: z.iso.datetime({ offset: true }),
  /** The earlier version this one replaced. */
  replacesId: z.uuid().nullable(),
});
export type DocumentView = z.infer<typeof DocumentView>;

export const DocumentList = z.object({ documents: z.array(DocumentView) });
export type DocumentList = z.infer<typeof DocumentList>;

/** APP-262 D10: a Spot nomination's kept chat thread, for its nominator and the Stock Admin team. */
export const SpotThread = z.object({ document: DocumentView });
export type SpotThread = z.infer<typeof SpotThread>;

/** The text fields of a multipart upload (the file travels as the `file` part). */
export const UploadDocumentFields = z
  .object({
    purpose: DocumentPurpose,
    lineId: z.uuid().optional(),
    staffOnly: z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .optional(),
  })
  .strict();
export type UploadDocumentFields = z.infer<typeof UploadDocumentFields>;

export const UploadDocumentResponse = z.object({ document: DocumentView });
export type UploadDocumentResponse = z.infer<typeof UploadDocumentResponse>;

export const DeleteDocumentBody = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type DeleteDocumentBody = z.infer<typeof DeleteDocumentBody>;

export const AwardDocumentsParams = z.object({ requestId: z.uuid() });
export const AwardDocumentParams = z.object({ requestId: z.uuid(), id: z.uuid() });
